import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Spin, Tabs } from 'antd';
import {
  STUDIO_PAGES,
  getStudio,
  getStudioOverview,
  studioAbilities,
  useStudioLoad,
  useStudioViewer,
} from '../api';
import CountPill from '../components/count-pill';
import StudioFormModal from '../list/studio-form-modal';
import ChangeManagerModal from '../list/change-manager-modal';
import StudioHeader, { StudioLoadError } from './studio-header';
import OverviewTab from './overview-tab';
import MembersTab from './members-tab';
import TeamsTab from './teams-tab';
import KcTab from './kc-tab';
import QueueTab from './queue-tab';
import LogTab from './log-tab';
import styles from '../styles.module.scss';

const TAB_KEYS = ['overview', 'members', 'teams', 'kc', 'queue', 'log'] as const;
type DetailTab = (typeof TAB_KEYS)[number];
const DEFAULT_TAB: DetailTab = 'overview';
const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

const isDetailTab = (value: string | null): value is DetailTab =>
  !!value && (TAB_KEYS as readonly string[]).includes(value);

/**
 * `/studio/:id` — Chi tiết Studio (SPEC Studio mục 8.1, 8.7.3–8.7.9): đầu trang + 6 tab qua `?tab=`
 * (overview mặc định, không ghi lên URL), đổi tab giữ `month`, bỏ tham số khác.
 * ADMIN, SMOD xem mọi Studio; Quản lý Studio chỉ Studio của mình (máy chủ trả 409 STUDIO_SCOPE → trang 403).
 * Nút theo quyền từ GET /studios/mine (useStudioViewer); mọi quyết định quyền do máy chủ kiểm.
 * Các tab chỉ gọi API sau khi S5 thành công, để người ngoài phạm vi chỉ sinh MỘT dòng nhật ký bị chặn.
 */
const StudioDetailPage = () => {
  const { id } = useParams();
  const studioId = Number(id);
  const validId = Number.isInteger(studioId) && studioId > 0;
  const [params, setParams] = useSearchParams();
  const viewer = useStudioViewer();
  const abilities = studioAbilities(viewer.mine, studioId);

  const requestedTab = params.get('tab');
  const activeTab: DetailTab = isDetailTab(requestedTab) ? requestedTab : DEFAULT_TAB;
  const rawMonth = params.get('month');
  const month = rawMonth && MONTH_PATTERN.test(rawMonth) ? rawMonth : undefined;

  const [editing, setEditing] = useState(false);
  const [changingManager, setChangingManager] = useState(false);

  const studio = useStudioLoad(() => getStudio(studioId), [studioId], {
    enabled: validId,
    resetKey: studioId,
  });
  const ready = !!studio.data;
  // S9: ô số tab Tổng quan và số `todo` trên nhãn tab Cần xử lý (một con số, mục 7.5).
  // Không gửi `month` của URL (bộ lọc tab Thành viên): tab Tổng quan luôn là tháng này (mục 8.7.4).
  const overview = useStudioLoad(() => getStudioOverview(studioId), [studioId], {
    enabled: validId && ready,
    resetKey: studioId,
  });

  const home = viewer.isManager
    ? { to: STUDIO_PAGES.mine, label: 'Về Studio của tôi' }
    : viewer.canViewAll
    ? { to: STUDIO_PAGES.list, label: 'Về danh sách Studio' }
    : { to: '/', label: 'Về Tổng quan' };

  const goTab = (key: string) => {
    const next = new URLSearchParams();
    if (month) next.set('month', month);
    if (key !== DEFAULT_TAB) next.set('tab', key);
    setParams(next);
  };
  const setMonth = (value: string) => {
    const next = new URLSearchParams(params);
    next.set('month', value);
    setParams(next, { replace: true });
  };
  const refreshCounts = () => overview.reload();
  const afterStudioEdit = () => {
    studio.reload();
    overview.reload();
  };

  if (!validId) {
    // Đường dẫn không phải số: hiện như Studio không tồn tại, không gọi API.
    return <StudioLoadError error={{ message: 'STUDIO_NOT_FOUND' }} home={home} />;
  }
  if (!studio.data) {
    if (studio.error) {
      return <StudioLoadError error={studio.error} onRetry={studio.reload} home={home} />;
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  const summary = studio.data;
  const todo = overview.data?.todo;

  return (
    <div className={styles.page}>
      <StudioHeader
        studio={summary}
        canAdmin={abilities.admin}
        onEdit={() => setEditing(true)}
        onChangeManager={() => setChangingManager(true)}
      />
      {studio.error && <StudioLoadError error={studio.error} onRetry={studio.reload} />}
      {viewer.error && !viewer.mine && (
        // Không đọc được GET /studios/mine: ẩn mọi nút thao tác (đóng an toàn) nhưng phải nói cho người xem biết.
        <Alert
          type="warning"
          showIcon
          message="Không tải được quyền của bạn nên các nút thao tác đang ẩn."
          action={
            <Button size="small" loading={viewer.loading} onClick={viewer.reload}>
              Thử lại
            </Button>
          }
        />
      )}

      <Tabs
        activeKey={activeTab}
        onChange={goTab}
        destroyOnHidden
        items={[
          {
            key: 'overview',
            label: 'Tổng quan',
            children: (
              <OverviewTab viewer={viewer} overview={overview} onOpenTab={(tab) => goTab(tab)} />
            ),
          },
          {
            key: 'members',
            label: 'Thành viên',
            children: (
              <MembersTab
                studioId={studioId}
                studio={summary}
                viewer={viewer}
                month={month}
                onMonthChange={setMonth}
                onChanged={refreshCounts}
              />
            ),
          },
          {
            key: 'teams',
            label: 'Nhóm nhảy',
            children: (
              <TeamsTab
                studioId={studioId}
                viewer={viewer}
                month={month}
                onChanged={refreshCounts}
              />
            ),
          },
          {
            key: 'kc',
            label: 'Kim cương',
            children: <KcTab studioId={studioId} viewer={viewer.mine} />,
          },
          {
            key: 'queue',
            label: (
              <span>
                Cần xử lý <CountPill count={todo} hideZero title="Việc cần xử lý" />
              </span>
            ),
            children: (
              <QueueTab
                studioId={studioId}
                viewer={viewer}
                overview={overview}
                onOpenTab={(tab) => goTab(tab)}
                onChanged={refreshCounts}
              />
            ),
          },
          {
            key: 'log',
            label: 'Lịch sử',
            children: <LogTab studioId={studioId} studio={summary} viewer={viewer} />,
          },
        ]}
      />

      {/* Modal của gói A2 (list/): chỉ dựng khi mở, để trang không gọi API gợi ý khi Admin chưa bấm. */}
      {abilities.admin && editing && (
        <StudioFormModal
          open
          studio={summary}
          onClose={() => setEditing(false)}
          onDone={afterStudioEdit}
        />
      )}
      {abilities.admin && changingManager && (
        <ChangeManagerModal
          open
          studio={summary}
          scopeMode={viewer.scopeMode}
          onClose={() => setChangingManager(false)}
          onDone={afterStudioEdit}
        />
      )}
    </div>
  );
};

export default StudioDetailPage;
